import { ObjectId } from "@novorender/data-js-api";

import { estimateBestShrunkSize, estimateShrunkSize, IdArrayShrinker, type ShrunkIdArray } from "utils/idArrayShrinker";

/** Below this many ids compaction is not attempted; the raw array is small enough. */
const COMPACT_MIN_IDS = 1024;

/**
 * Just a thin wrapper on top of ordered object ID array.
 * Trying to keep this interface as limited as possible.
 * Issue with native sets:
 * - Not big enough (up to ~16 million elements, we already need at least 20 million)
 * - Higher memory consumption
 *
 * Group ids are loaded when a group is activated but were never released on
 * deactivation, so a session of toggling groups accumulated every activated
 * group's full id array. The set therefore keeps two representations: the
 * expanded sorted `Uint32Array` while hot, and a compressed `ShrunkIdArray`
 * (bitmap/RLE, see `idArrayShrinker`) after `compact()`. Iteration re-expands
 * lazily; the compressed form is kept so re-compacting a toggled group is free.
 *
 * There's no `has` method because it's not needed at the moment,
 * but I expect it can be demanded at some point (e.g. a feature to show all groups containing selecting object),
 * so I'm hesitant to optimize it based on the lack of has method.
 */
export class ObjectIdSet {
    /** Expanded form; present while hot. Rebuilt from `shrunk` on demand. */
    private data: Uint32Array | undefined;
    /** Compressed form; built by the first `compact()` that pays off. */
    private shrunk: ShrunkIdArray | undefined;
    /** Set when shrinking was attempted and would not save memory. */
    private incompressible = false;

    constructor(numbers: Iterable<ObjectId>) {
        this.data = new Uint32Array(numbers).sort();
        this.removeDuplicates();
    }

    get size(): number {
        return this.data ? this.data.length : this.shrunk!.length;
    }

    /**
     * We need to pass sorted iterables to highlights, so since array is sorted already (but it's an implementation detail),
     * add this shortcut to avoid creating a new array.
     */
    asSortedIterable(): Iterable<number> {
        return this.expanded();
    }

    *[Symbol.iterator](): IterableIterator<number> {
        for (const num of this.expanded()) {
            yield num;
        }
    }

    /**
     * Drop the expanded array, keeping only the compressed form. Call for sets
     * that are cold (e.g. a deactivated group's ids); the next read re-expands.
     * No-op when the set is small or does not compress to less than the raw
     * 4-bytes-per-id array.
     */
    compact(): void {
        if (!this.data || this.data.length < COMPACT_MIN_IDS || this.incompressible) {
            return;
        }
        if (!this.shrunk) {
            if (estimateBestShrunkSize(this.data) >= this.data.byteLength) {
                this.incompressible = true;
                return;
            }
            this.shrunk = IdArrayShrinker.shrink(this.data);
        }
        this.data = undefined;
    }

    /** Approximate JS-heap bytes held by this set, for memory diagnostics. */
    get residentBytes(): number {
        return (this.data?.byteLength ?? 0) + (this.shrunk ? estimateShrunkSize(this.shrunk) : 0);
    }

    get isCompacted(): boolean {
        return this.data === undefined;
    }

    private expanded(): Uint32Array {
        if (!this.data) {
            this.data = IdArrayShrinker.expand(this.shrunk!);
        }
        return this.data;
    }

    private removeDuplicates() {
        const data = this.data!;
        if (data.length < 2) {
            return;
        }

        let index = 1;

        for (let i = 1; i < data.length; i++) {
            if (data[i] !== data[i - 1]) {
                data[index] = data[i];
                index++;
            }
        }

        if (index !== data.length) {
            // The expectation is that there are few duplicates, so it's not worth to make a new array with slice.
            this.data = data.subarray(0, index);
        }
    }
}
